import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import { Row, Col, Spin } from "antd";
import moment from "moment";
import LabsStockTableSection from "./tableSection";
import syncIcon from "../../assets/sync-alt.png";
import { syncLabStock, fetchLastSyncTime } from "./redux/actions";
import { parseNotificationParams } from "../../components/Notification/notificationRoutes";
import { getLSKey, LS_KEYS } from "../../utils/localStorageHelper";

const LabsStockManagement = () => {
  const dispatch = useDispatch();
  const { lab_id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Check for section and expand parameters in URL
  const { section: sectionParam, expandRegisterId } = parseNotificationParams(searchParams.toString());
  const expandId = expandRegisterId ? parseInt(expandRegisterId) : null;

  const { labs } = useSelector((state) => state.authReducer);
  const { syncingLabStock, lastSynced } = useSelector((state) => state.labsStockManagementReducer);

  const [syncedTime, setSyncedTime] = useState(() => {
    const stored = localStorage.getItem("labStockLastSynced");
    return stored || null;
  });

  useEffect(() => {
    dispatch(fetchLastSyncTime());
  }, [dispatch, lab_id]);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(fetchLastSyncTime());
    }, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    if (lastSynced) {
      setSyncedTime(lastSynced);
      localStorage.setItem("labStockLastSynced", lastSynced);
    }
  }, [lastSynced]);

  const formattedSyncTime = syncedTime ? moment(syncedTime).format("DD MMM YYYY, HH:mm") : null;

  const selectedLab = labs?.find((lab) => (lab?.labSetup?.id || lab?.id) == lab_id);
  const user = useSelector((state) => state.authReducer.user);
  const lsselectedLabsKey = getLSKey(LS_KEYS.SELECTED_LABS, user?.id);
  const lsLab = JSON.parse(localStorage.getItem(lsselectedLabsKey) || "[]")?.find((lab) => (lab?.labSetup?.id || lab?.id) == lab_id);

  const labName = selectedLab?.labSetup?.labName || selectedLab?.labName || lsLab?.labSetup?.labName || lsLab?.labName;
  const branchCode = selectedLab?.labSetup?.branchCode || selectedLab?.branchCode || lsLab?.labSetup?.branchCode || lsLab?.branchCode;

  const handleSync = () => {
    dispatch(syncLabStock());
  };

  // Generate notification action for table section from URL parameters
  const tableNotificationAction = React.useMemo(() => {
    if (sectionParam === 'variance-reports' && expandId) {
      const action = { 
        type: 'VARIANCE',
        action: { expandSection: 'variance-reports' },
        linkedId: expandId,
        labSetupId: parseInt(lab_id),
        relatedIds: {
          stockRegisterId: expandId,
          labSetupId: parseInt(lab_id)
        },
        timestamp: Date.now()
      };
      
      return action;
    }
    return null;
  }, [sectionParam, expandId, lab_id]);

  // Clear URL parameters after successful expansion
  const clearExpansionParams = () => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      newParams.delete('section');
      newParams.delete('expand');
      return newParams.toString();
    });
  };

  return (
    <Row className="page-top-div">
      <Col span={24}>
        <Row style={{ justifyContent: "space-between" }}>
          <Col>
            <span className="text-span" style={{ lineHeight: "17px" }}>
              {labName ? `${labName}${branchCode ? ` (${branchCode})` : ''}` : "Lab Name..."}
            </span>
          </Col>
          <Col>
            <div style={{ display: "flex", alignItems: "center" }}>
              <span
                style={{
                  color: "#000000",
                  fontFamily: "open-sans, Arial",
                  fontSize: "10px",
                  marginRight: "4px"
                }}
              >
                {formattedSyncTime ? `Last Synced: ${formattedSyncTime}` : "Last Synced: --"}
              </span>
              {syncingLabStock ? (
                <Spin
                  size="small"
                />
              ) : (
                <img
                  src={syncIcon}
                  alt="sync"
                  style={{ width: "14px", height: "14px", cursor: "pointer" }}
                  onClick={handleSync}
                />
              )}
            </div>
          </Col>
        </Row>
        <LabsStockTableSection 
          notificationAction={tableNotificationAction} 
          clearExpansionParams={clearExpansionParams}
        />
      </Col>
    </Row>
  );
};

export default LabsStockManagement;
